<template lang="pug">
.background(ref="backgroundRef" :style="style" :class="bgImg")
</template>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { useMQ } from '../../js/mq'
import { useRogue } from '../../js/rogue'

const mq = useMQ()
const rogue = useRogue()

// Aspect ratios of different image backgrounds
const BG = {
  'wall-tall': {
    width  : 2000,
    height : 1796,
  },
  angled: {
    width  : 2000,
    height : 911,
  },
  wall: {
    width  : 2000,
    height : 1311,
  },
  dark: {
    width  : 2000,
    height : 733,
  },
}

const props = defineProps({
  product: {
    type     : Object,
    required : true,
  },
})

const emit = defineEmits(['height'])

const backgroundRef = ref<HTMLElement>()

const style = ref({
  'background-size'       : '120%',
  'background-position-x' : null as string | null,
  'background-position-y' : null as string | null,
})

const bgImg = computed(() => (props.product.roguebuilder.props || {}).background)

function onResize(bgElSize: DOMRectReadOnly) {
  const rbProps = props.product.roguebuilder.props

  // For scalable pages, the RHPA needs to move down (when scaled) by the height of this area
  emit('height', bgElSize.height)

  // The rest of the file is just about sizing / positioning the image
  if (!bgImg.value || rbProps.background === 'gray') {
    return
  }

  // These are set in Roguebuilder
  const bgRatio = Number(rbProps.bgRatio || 4)
  const bgOffset = Number(rbProps.bgOffset || 0)

  // Since the image is css position: fit, the dimensions of the actual image might not match the image element dimensions
  // So we pull them from Roguebuilder
  const imgHeight = Number(rbProps.imgHeight || 1000)
  const imgWidth = Number(rbProps.imgWidth || 1000)

  // Each background size has a specific aspect ratio that we need to position and size it
  const bgSize = BG[bgImg.value]
  
  // Handle unknown background types gracefully
  if (!bgSize) {
    const backgroundType = bgImg.value || 'null/undefined'
    const error = new Error(`Unknown background type: ${backgroundType}`)
    const url = typeof window !== 'undefined' ? window.location.href : (rogue.router?.currentRoute.value?.fullPath || 'unknown')
    
    rogue.log?.error(
      'OverflowBackground: Unknown background type encountered',
      error,
      {
        backgroundType,
        url,
        rbProps     : rbProps,
        productSku  : props.product?.sku || props.product?.id || 'unknown',
        elementSize : {
          width  : bgElSize.width,
          height : bgElSize.height,
        },
        availableBackgroundTypes: Object.keys(BG),
      }
    )
    
    return
  }

  // How big are we currently compared to the product image
  // Other sizes are based on the product image, so this will be used for other ratios
  const currentRatio = (bgElSize.height / imgHeight)

  // The background is ratioed the same as the product image, so we can compute the background size
  const bgHeight = bgRatio * bgSize.height * currentRatio
  const bgWidth = bgHeight * (bgSize.width / bgSize.height)

  // Size and position the Y of the background image
  style.value['background-size'] = `${bgWidth}px ${bgHeight}px`

  if (bgOffset) {
    style.value['background-position-y'] = `${-1 * (bgOffset * currentRatio)}px`
  }

  // To position the left/right of the image, we need to know how wide the actual image is.
  const imgRatio = imgWidth / imgHeight
  const currentImgWidth = imgRatio * bgElSize.height

  // Mobile always is 100% width, so its easier
  if (['mobile', 'tablet'].includes(mq.value)) {
    if (rbProps.background === 'angled') {
      // Angled background is always centered beneath the image, and the image is centered in the element
      const left = (bgWidth - bgElSize.width) / 2
      style.value['background-position-x'] = `${-1 * left}px`
    } else {
      // The other backgrounds are supposed to line up the left of the product image to x=0 on the background
      // The padding on each side (bgElSize.width - currentImgWidth) is the same, so to get the left offset
      // is just a divide by 2
      const padding = (bgElSize.width - currentImgWidth) / 2
      style.value['background-position-x'] = `${padding}px`
    }
  } else {
    // On desktop, the image changes from full width to partial width, and has different margins
    // We get the actual product image to use its rectangle, instead of trying to compute paddings etc
    // @todo could maybe be passed in as a ref?
    const mediaEl = document.querySelector('#pdp-media img')
    const mediaRect = mediaEl!.getBoundingClientRect()

    if (rbProps.background === 'angled') {
      // The background by default is at x origin 0.
      // We need to shift it left until the center of the background image lines up with the center of the product
      // Essentially: move right so that background lines up with x=0 of the product image
      // Then move left so that it is centered
      const imgCenter = (mediaRect.width / 2)
      const bgCenter = (bgWidth / 2)
      let padding = imgCenter - bgCenter
      if (mq.value === 'xl-desktop') {
        // XL desktop can detach the product image from the edge of the screen, wheras the background el overflows
        padding += mediaRect.left
      }
      style.value['background-position-x'] = `${padding}px`
    } else {
      // Line up x=0 of the background image to the left edge of the product image
      let padding = (mediaRect.width - currentImgWidth) / 2
      if (mq.value === 'xl-desktop' && mediaRect.left > 16) {
        // XL desktop can detach the product image from the edge of the screen, wheras the background el overflows
        padding += mediaRect.left
      }
      style.value['background-position-x'] = `${padding}px`
    }
  }
}

function resize() {
  if (backgroundRef.value) {
    onResize(backgroundRef.value.getBoundingClientRect())
  }
}

useResizeObserver(backgroundRef, resize)

onMounted(() => {
  nextTick(() => resize)
  window.addEventListener('resize', resize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resize)
})
</script>
<style lang="scss" scoped>
.background {
  background-color: $color-gray-x-light;

  &.wall {
    background-image: url("https://assets.roguefitness.com/dynamic/rack-bg.jpg");
    background-repeat: repeat-x;
  }

  &.wall-tall {
    background-image: url("https://assets.roguefitness.com/dynamic/rack-bg-tall.jpg");
    background-repeat: repeat-x;
  }

  &.angled {
    background-image: url("https://assets.roguefitness.com/dynamic/angled-bg.jpg");
    background-repeat: no-repeat;
    background-color: white;
  }

  &.dark {
    background-image: url("https://assets.roguefitness.com/dynamic/dark-bg_ivohgw.png");
    background-repeat: no-repeat;
    background-color: black;
  }
}
</style>
